import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable, ObservedValueOf } from "rxjs";
import { tap } from "rxjs/operators";
import { HttpParams } from "@angular/common/http";
import { GpsDashboardResponse } from "../interfaces/gps.interface";

type GpsAction =
  | "request_installation"
  | "paused"
  | "cancel"
  | "resume"
  | "restore"
  | "accept_install_date"
  | "provider_propose_date"
  | "provider_accept_date"
  | "schedule_installation"
  | "provider_finish_installation"
  | "provider_upload_installation_order";

type GpsDashboardFilters = Partial<{
  status: string;
  statuses: string[];
  provider_id: string;
  scheduled_at_date: string;
  scheduled_at_range: string[];
  sort_by: string;
  sort_order: string;
  search: string[];
  page_number: number;
  page_size: number;
}>;

@Injectable()
export class GpsService {
  constructor(private http: HttpClientService) {}

  public getGps(account_id): Observable<any[]> {
    return this.http.get(`/api/accounts/${account_id}/gps`).pipe(tap((_) => console.log("GpsService.getGps: Ok.")));
  }

  public with_display_data(gps_id): Observable<any[]> {
    return this.http
      .get(`/api/gps/${gps_id}/with_display_data`)
      .pipe(tap((_) => console.log("GpsService.withDisplayData: Ok.")));
  }

  public getGpsInformation(): Observable<any[]> {
    return this.http.get("/api/gps/information").pipe(tap((_) => console.log("GpsService.getGpsInformation: Ok.")));
  }

  public getProviders(): Observable<any[]> {
    return this.http.get("/api/gps/providers").pipe(tap((_) => console.log("GpsService.getProviders: Ok.")));
  }

  public getProvider(id: number): Observable<any> {
    return this.http.get(`/api/gps/providers/${id}`).pipe(tap((_) => console.log("GpsService.getProvider: Ok.")));
  }

  public getAllGps(filters): Observable<any> {
    const options = {
      params: filters,
    };
    return this.http.get("/api/gps/all", options).pipe(tap((_) => console.log("GpsService.getAllGps: Ok.")));
  }

  public getGpsDashboardList(filters: GpsDashboardFilters): Observable<GpsDashboardResponse> {
    const options = {
      params: new HttpParams({
        fromObject: {
          "search[]": filters.search,
          provider_id: filters.provider_id,
          ...(filters.statuses ? { "status[]": filters.statuses } : { status: filters.status }),
          page_number: filters.page_number,
          page_size: filters.page_size,
          scheduled_at_date: filters.scheduled_at_date,
          "scheduled_at_range[]": filters.scheduled_at_range,
          sort_by: filters.sort_by,
          sort_order: filters.sort_order,
        },
      }),
    };
    return this.http
      .get("/api/gps/dashboard", options)
      .pipe(tap((_) => console.log("GpsService.getGpsDashboardList: Ok.")));
  }

  public downloadDashboardCsv(filters: GpsDashboardFilters): Observable<Blob> {
    const cleanFilters: any = {
      "search[]": filters.search,
      provider_id: filters.provider_id,
      ...(filters.statuses ? { "status[]": filters.statuses } : { status: filters.status }),
      scheduled_at_date: filters.scheduled_at_date,
      "scheduled_at_range[]": filters.scheduled_at_range,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
    };

    // Remove undefined keys
    Object.keys(cleanFilters).forEach((key) => cleanFilters[key] === undefined && delete cleanFilters[key]);

    const options = {
      params: new HttpParams({ fromObject: cleanFilters }),
      responseType: "blob" as "json",
    };
    return this.http
      .get("/api/gps/dashboard_to_csv", options)
      .pipe(tap((_) => console.log("GpsService.downloadDashboardCsv: Ok.")));
  }

  public addGps(account_id, gps): Observable<any> {
    const options = {
      gps,
    };
    return this.http
      .post(`/api/accounts/${account_id}/gps`, options)
      .pipe(tap((_) => console.log("GpsService.addGps: Ok.")));
  }
  public updateGps(gps): Observable<any> {
    const options = {
      gps,
    };
    return this.http.put(`/api/gps/${gps.id}`, options).pipe(tap((_) => console.log("GpsService.updateGps: Ok.")));
  }

  public perform(gps_id, action: GpsAction, params?): Observable<any> {
    const options = {
      gps_action: action,
      ...params,
    };
    return this.http
      .post(`/api/gps/${gps_id}/perform`, options)
      .pipe(tap((_) => console.log("GpsService.performGpsAction: Ok.")));
  }

  public suspend(gps_id, avalaible_at_agency, expected_arrival_date): Observable<any> {
    const options = {
      avalaible_at_agency,
      expected_arrival_date,
    };
    return this.http
      .post(`/api/gps/${gps_id}/suspend`, options)
      .pipe(tap((_) => console.log("GpsService.suspend: Ok.")));
  }

  public getIncidentReasons(): Observable<any> {
    return this.http
      .get("/api/gps/incident_reasons")
      .pipe(tap((_) => console.log("GpsService.getIncidentReasons: Ok.")));
  }

  public getInstallationDetailsPdf(gps_id): Observable<any> {
    return this.http
      .get(`/api/gps/${gps_id}/installation_appointment_details`, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("GpsService.getInstallationDetailsPdf: Ok.")));
  }

  public createIncident(gps_id: number, incident_type: string, incident_comment: string): Observable<any> {
    const options = { incident_type, incident_comment };
    return this.http
      .post(`/api/gps/${gps_id}/incident`, options)
      .pipe(tap((_) => console.log("GpsService.createIncident: Ok.")));
  }

  public validateInstallation(gps_id): Observable<any> {
    return this.http
      .post(`/api/gps/${gps_id}/validate_installation`, {})
      .pipe(tap((_) => console.log("GpsService.validateInstallation: Ok.")));
  }

  public notifyKeyNotInDealership(id: number): Observable<any> {
    const options = {
      id,
    };
    return this.http
      .post(`/api/gps/${id}/notify_key_not_in_dealership`, options)
      .pipe(tap((_) => console.log("GpsService.notifyKeyNotInDealership: Ok.")));
  }

  public loadInfo(content): Observable<any> {
    return this.http
      .post("/api/gps/load_key_collected", { content })
      .pipe(tap((_) => console.log("GpsService.loadInfo: Ok.")));
  }
}
